import React, { useEffect, useState } from "react";
import { getListPost } from "../service";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ItemCommunity from "../components/item.community";
import { useInfiniteScroll } from "../components/useInfiniteScroll";
import CustomSnackbar from "components/snackbar";
import FilterCommunity, {
  CommunityFilterValues,
  EMPTY_COMMUNITY_FILTER,
} from "../components/filter.community";
import { buildListParams } from "../utils/build-list-params";

const Community = () => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  // Raw filter state, reflected in the UI immediately. Only the keyword
  // field is debounced before it drives a refetch — the author/status/date
  // controls are discrete selections and should refetch as soon as they
  // change.
  const [filter, setFilter] = useState<CommunityFilterValues>(EMPTY_COMMUNITY_FILTER);
  const [debouncedKeyword, setDebouncedKeyword] = useState(filter.keyword);

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedKeyword(filter.keyword);
    }, 500);
    return () => clearTimeout(delay);
  }, [filter.keyword]);

  const effectiveFilter: CommunityFilterValues = { ...filter, keyword: debouncedKeyword };

  const [listLoading, setListLoading] = useState(true);
  // null = no error, "" = failed with no server-provided message.
  const [listError, setListError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // autoLoad is disabled because this effect below owns fetching page 1 (so
  // it can catch and surface errors); the hook is only used here for
  // scroll-driven paging from page 2 onward.
  const { items: posts, loading, reset, removeItem } = useInfiniteScroll(
    getListPost,
    buildListParams(effectiveFilter),
    {
      initialLimit: 10,
      threshold: 100,
      autoLoad: false,
    }
  );

  // Fetch page 1 on mount and whenever the effective filter changes, pushing
  // the result into the hook via reset() rather than relying on loadMore's
  // own memoization (see useInfiniteScroll's paramsRef comment). This is the
  // single source of the page-1 request — a fetch failure (e.g. a backend
  // 500) must be visibly different from a genuinely empty result, so it's
  // caught here instead of relying on the hook's own error handling.
  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    getListPost(buildListParams(effectiveFilter))
      .then((res) => {
        if (cancelled) return;
        setListError(null);
        reset(res.data);
      })
      .catch((error) => {
        if (cancelled) return;
        reset([]);
        setListError(error?.response?.data?.message ?? "");
        setSnackbarSeverity("error");
        setSnackbarMessage("Không tải được danh sách bài viết, vui lòng thử lại.");
        setSnackbarOpen(true);
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedKeyword,
    effectiveFilter.authorId,
    effectiveFilter.status,
    effectiveFilter.dateFrom,
    effectiveFilter.dateTo,
    reloadKey,
  ]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDeleted = (id: string) => {
    removeItem(id);
    setSnackbarOpen(true);
    setSnackbarSeverity("success");
    setSnackbarMessage("Xóa bài viết thành công");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: "1450px",
        px: { xs: 1, sm: 2, md: 2 },
      }}
    >
      <Typography variant="h5" fontWeight={550} mb={2} mt={2}>
        Phê duyệt bài viết
      </Typography>

      <FilterCommunity value={filter} onChange={setFilter} />

      {posts.map((post: any, index) => (
        <ItemCommunity
          setSnackbarOpen={setSnackbarOpen}
          setSnackbarSeverity={setSnackbarSeverity}
          setSnackbarMessage={setSnackbarMessage}
          onDeleted={handleDeleted}
          key={post._id ?? index}
          post={post}
        />
      ))}

      {(listLoading || loading) && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {!listLoading && !loading && listError !== null && (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="body1" color="error">
            Không tải được danh sách bài viết.
          </Typography>
          {listError !== "" && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {listError}
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 2 }}
            onClick={() => setReloadKey((k) => k + 1)}
          >
            Thử lại
          </Button>
        </Box>
      )}

      {!listLoading && !loading && listError === null && posts.length === 0 && (
        <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
          <Typography variant="body1">Không tìm thấy bài viết nào phù hợp.</Typography>
        </Box>
      )}

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default Community;
